import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Accessibility,
  ArrowRight,
  Briefcase,
  Building2,
  Compass,
  GraduationCap,
  Landmark,
  Laptop,
  MessagesSquare,
  Search,
  Send,
  UserPlus,
  Users,
} from "lucide-react";
import { useFetch } from "../lib/api";
import { btn, EmptyState, Loading, Badge } from "../components/kit";
import { JobCard, ProjectCard, GovCard } from "../components/cards";

const TYPES = [
  { v: "jobs", label: "Looking for a Job", icon: Briefcase, to: "/jobs", ph: "Job title, skill or employer" },
  { v: "freelance", label: "Looking for Freelance Work", icon: Laptop, to: "/freelancing", ph: "Project, skill or category" },
  { v: "gov", label: "Government Jobs & Schemes", icon: Landmark, to: "/government", ph: "Department, scheme or benefit" },
];

const CATEGORIES = [
  {
    icon: Briefcase,
    title: "Private-sector jobs",
    body: "Current India-eligible roles from connected sources.",
    to: "/jobs",
  },
  {
    icon: Landmark,
    title: "PwD-eligible government jobs",
    body: "Official vacancies with reservation evidence when available.",
    to: "/government?kind=job",
  },
  {
    icon: Accessibility,
    title: "Government schemes & benefits",
    body: "Official disability-related schemes and service links.",
    to: "/government?kind=scheme",
  },
  {
    icon: GraduationCap,
    title: "Internships & apprenticeships",
    body: "Current India-eligible training and early-career roles.",
    to: "/jobs?employment_type=internship",
  },
  {
    icon: Laptop,
    title: "Remote & hybrid jobs",
    body: "Roles that explicitly accept candidates located in India.",
    to: "/jobs?work_mode=remote",
  },
  {
    icon: Users,
    title: "Disability-inclusive employers",
    body: "Roles with employer-provided accessibility information.",
    to: "/jobs?accessibility=true",
  },
];

const QUICK_FILTERS = [
  ["PwD-confirmed government jobs", "/government?kind=job&pwd_eligibility=PwD%20Eligibility%20Confirmed"],
  ["PwD-confirmed private jobs", "/jobs?pwd_eligibility=PwD%20Eligibility%20Confirmed"],
  ["Government jobs", "/government?kind=job"],
  ["Remote opportunities", "/jobs?work_mode=remote"],
  ["Fresher jobs", "/jobs?experience_level=entry"],
  ["Internships", "/jobs?employment_type=internship"],
];

const PROFESSIONS = [
  "AML / KYC",
  "UI / UX",
  "Graphic Design",
  "Technology",
  "Finance",
  "HR",
  "Research",
  "Marketing",
  "Customer Support",
];

function Selector() {
  const [type, setType] = useState("jobs");
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const t = TYPES.find((x) => x.v === type);
  return (
    <form role="search" className="card p-5 md:p-6 mt-10" onSubmit={(e) => { e.preventDefault(); nav(`${t.to}${q ? `?q=${encodeURIComponent(q)}` : ""}`); }} data-testid="opportunity-selector">
      <fieldset>
        <legend className="font-semibold text-ink mb-3">What are you looking for?</legend>
        <div className="grid gap-3 sm:grid-cols-3">
          {TYPES.map((o) => (
            <label key={o.v} className={`flex min-h-[56px] cursor-pointer items-center gap-3 rounded-lg border-2 px-4 font-semibold ${type === o.v ? "border-blue bg-mist text-navy" : "border-line text-ink hover:border-field"}`}>
              <input type="radio" name="opp-type" value={o.v} checked={type === o.v} onChange={() => setType(o.v)} className="h-5 w-5 accent-[rgb(var(--c-blue))]" data-testid={`opportunity-type-${o.v}`} />
              <o.icon className="h-5 w-5 shrink-0" aria-hidden="true" />{o.label}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <label htmlFor="page-search" className="sr-only">Search {t.label.toLowerCase()}</label>
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-sub" aria-hidden="true" />
          <input id="page-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.ph} className="field !min-h-[52px] pl-10" data-testid="home-search-input" />
        </div>
        <button className={btn("primary")} data-testid="home-search-submit">Search</button>
      </div>
    </form>
  );
}

function Featured({ title, to, path, render, empty, testid }) {
  const { data, loading } = useFetch(path);
  return (
    <section className="container-x py-12" aria-labelledby={`${testid}-h`} data-testid={testid}>
      <div className="flex items-end justify-between gap-4">
        <h2 id={`${testid}-h`} className="text-2xl md:text-3xl font-bold">{title}</h2>
        <Link
          to={to}
          className="link inline-flex min-h-[44px] items-center gap-1 font-semibold"
          data-testid={`${testid}-all-link`}
        >
          See all<span className="sr-only"> {title.toLowerCase()}</span>
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
      <div className="mt-6">
        {loading ? <Loading /> : data?.items?.length ? <ul className="grid gap-4 md:grid-cols-3">{data.items.map((x) => <li key={x.id} className="flex [&>*]:w-full">{render(x)}</li>)}</ul> : <EmptyState title={empty} body="New listings appear here as soon as they are approved." />}
      </div>
    </section>
  );
}

function OpportunityCategories() {
  return (
    <section className="container-x py-12" aria-labelledby="categories-h" data-testid="opportunity-categories">
      <p className="eyebrow">Explore opportunities</p>
      <h2 id="categories-h" className="mt-2 text-2xl font-bold md:text-3xl">
        Find the right path for you
      </h2>
      <ul className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {CATEGORIES.map(({ icon: Icon, title, body, to }) => (
          <li key={title}>
            <Link
              to={to}
              className="card flex min-h-full gap-4 p-5 transition-[border-color,transform] hover:-translate-y-0.5 hover:border-blue"
              data-testid={`home-category-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-mist text-navy">
                <Icon className="h-5 w-5" aria-hidden="true" />
              </span>
              <span>
                <span className="block font-bold text-navy">{title}</span>
                <span className="mt-1 block text-sm text-sub">{body}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function BrowseProfessions() {
  return (
    <section className="container-x py-12" aria-labelledby="professions-h" data-testid="browse-professions">
      <p className="eyebrow">Browse by profession</p>
      <h2 id="professions-h" className="mt-2 text-2xl font-bold md:text-3xl">
        Explore professional roles
      </h2>
      <div className="mt-6 flex flex-wrap gap-3">
        {PROFESSIONS.map((profession) => (
          <Link
            key={profession}
            to={`/jobs?q=${encodeURIComponent(profession)}`}
            className={btn("secondary", "sm")}
            data-testid={`profession-${profession.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            {profession}
          </Link>
        ))}
      </div>
    </section>
  );
}

const STEPS = [
  [UserPlus, "Create one account", "Choose the roles that fit you — job seeker, freelancer, employer, client or mentor. Disclosure of disability is never required."],
  [Compass, "Discover opportunities", "Search jobs, freelance projects and government listings with filters for work arrangement and accessibility information."],
  [Send, "Apply and track", "Apply with your CV, send proposals and follow every status change from your dashboard."],
  [MessagesSquare, "Grow with the community", "Ask questions, request mentorship or a resume review, and learn from professionals."],
];

export default function Home() {
  const posts = useFetch("/posts");
  return (
    <>
      <section className="bg-surface border-b border-line" aria-labelledby="hero-h">
        <div className="container-x py-14 md:py-20 grid gap-10 lg:grid-cols-[1.4fr_1fr] items-center">
          <div>
            <p className="eyebrow">OpenPath · Every Ability. More Possibilities.</p>
            <h1 id="hero-h" className="mt-3 text-4xl font-extrabold sm:text-5xl lg:text-6xl" data-testid="hero-headline">
              Find Opportunities That Move You Forward.
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-sub md:text-xl">
              Discover jobs, internships, government vacancies and schemes in one accessible platform,
              with transparent eligibility information and direct application links.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/jobs" className={btn("primary")} data-testid="explore-opportunities-btn">Explore Opportunities</Link>
              <Link to="/community" className={btn("community")} data-testid="join-community-btn">Join the Community</Link>
            </div>
            <div className="mt-6 flex flex-wrap gap-2" aria-label="Quick opportunity filters">
              {QUICK_FILTERS.map(([label, to]) => (
                <Link
                  key={label}
                  to={to}
                  className={btn("secondary", "sm")}
                  data-testid={`quick-filter-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {label}
                </Link>
              ))}
            </div>
          </div>
          <div className="decor hidden lg:block" aria-hidden="true">
            <div className="card p-6 space-y-4">
              {[[Briefcase, "Jobs", "Full-time, part-time, internships"], [Laptop, "Freelancing", "Projects and proposals"], [Landmark, "Government", "Vacancies, schemes and guides"], [Users, "Community", "Mentors, referrals and advice"]].map(([I, t, d]) => (
                <div key={t} className="flex items-center gap-4 rounded-lg bg-surface p-4"><span className="grid h-11 w-11 place-items-center rounded-lg bg-white border border-line text-navy"><I className="h-5 w-5" /></span><div><p className="font-semibold text-navy">{t}</p><p className="text-sm text-sub">{d}</p></div></div>
              ))}
            </div>
          </div>
        </div>
        <div className="container-x pb-14 -mt-6"><Selector /></div>
      </section>

      <OpportunityCategories />
      <div className="bg-surface">
        <Featured title="Latest jobs" to="/jobs" path="/jobs?limit=3" render={(j) => <JobCard job={j} />} empty="No verified jobs yet" testid="latest-jobs" />
      </div>
      <Featured title="Government opportunities" to="/government?kind=job" path="/gov?kind=job&limit=3" render={(g) => <GovCard g={g} />} empty="No verified government vacancies are available yet" testid="latest-gov-jobs" />
      <div className="bg-surface">
        <Featured title="Government schemes & benefits" to="/government?kind=scheme" path="/gov?kind=scheme&limit=3" render={(g) => <GovCard g={g} />} empty="No verified schemes are available yet" testid="latest-schemes" />
      </div>
      <Featured title="Latest internships & apprenticeships" to="/jobs?employment_type=internship" path="/jobs?employment_type=internship&limit=3" render={(j) => <JobCard job={j} />} empty="No verified internships are available yet" testid="latest-internships" />
      <div className="bg-surface"><Featured title="Featured freelance projects" to="/freelancing" path="/projects?featured=true&limit=3" render={(p) => <ProjectCard p={p} />} empty="No featured projects yet" testid="featured-projects" /></div>
      <BrowseProfessions />

      <section className="bg-surface py-12" aria-labelledby="comm-h" data-testid="community-preview">
        <div className="container-x grid gap-8 lg:grid-cols-[1fr_1.4fr]">
          <div>
            <p className="eyebrow !text-purple">OpenPath Community</p>
            <h2 id="comm-h" className="mt-2 text-2xl md:text-3xl font-bold">Learn, ask and connect with professionals</h2>
            <p className="mt-3 text-sub">Join discussions on career advice, interviews, workplace accessibility and more. Request mentorship, a resume review or interview preparation from people in the directory.</p>
            <div className="mt-6 flex flex-wrap gap-3"><Link to="/community" className={btn("community")}>Visit the community</Link><Link to="/community/directory" className={btn("secondary")}>Find a mentor</Link></div>
          </div>
          <div>
            {posts.loading ? <Loading /> : posts.data?.items?.length ? (
              <ul className="space-y-3">{posts.data.items.slice(0, 3).map((p) => (
                <li key={p.id} className="card p-4"><Badge tone="purple">{p.category}</Badge><h3 className="mt-2 text-lg font-semibold"><Link to={`/community/posts/${p.id}`} className="hover:underline">{p.title}</Link></h3><p className="text-sm text-sub">{p.author_name} · {p.comment_count} replies</p></li>
              ))}</ul>
            ) : <EmptyState icon={MessagesSquare} title="No discussions yet" body="Be the first to start a conversation." action={<Link to="/community" className={btn("community", "sm")}>Start a discussion</Link>} />}
          </div>
        </div>
      </section>

      <section className="container-x py-14" aria-labelledby="how-h">
        <h2 id="how-h" className="text-2xl md:text-3xl font-bold">How OpenPath works</h2>
        <ol className="mt-8 grid gap-6 md:grid-cols-4">
          {STEPS.map(([I, t, d], i) => (
            <li key={t} className="card p-6"><span className="grid h-11 w-11 place-items-center rounded-lg bg-mist text-navy"><I className="h-5 w-5" aria-hidden="true" /></span><p className="mt-4 text-sm font-semibold text-sub">Step {i + 1}</p><h3 className="text-lg font-bold">{t}</h3><p className="mt-2 text-sub">{d}</p></li>
          ))}
        </ol>
      </section>

      <section className="container-x pb-14" aria-labelledby="cta-h">
        <h2 id="cta-h" className="sr-only">For employers and professionals</h2>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="on-dark rounded-xl bg-navy p-8 text-white">
            <Building2 className="h-8 w-8" aria-hidden="true" />
            <h3 className="mt-4 text-2xl font-bold text-white">Hiring? Reach skilled, diverse talent</h3>
            <p className="mt-2 text-white/90">Post roles, share your workplace accommodation policy and manage applicants in one place.</p>
            <Link to="/employers" className={`${btn("secondary")} mt-6`} data-testid="employer-cta">For employers</Link>
          </div>
          <div className="rounded-xl border-2 border-purple p-8">
            <GraduationCap className="h-8 w-8 text-purple" aria-hidden="true" />
            <h3 className="mt-4 text-2xl font-bold">Share your expertise as a mentor</h3>
            <p className="mt-2 text-sub">Recruiters, counsellors, coaches and accessibility specialists can offer guidance through the professional directory.</p>
            <Link to="/professionals" className={`${btn("community")} mt-6`} data-testid="professional-cta">For professionals & mentors</Link>
          </div>
        </div>
      </section>

      <section className="bg-mist py-14" aria-labelledby="a11y-h">
        <div className="container-x grid gap-6 md:grid-cols-[auto_1fr] items-start">
          <Accessibility className="h-12 w-12 text-navy" aria-hidden="true" />
          <div className="max-w-3xl">
            <h2 id="a11y-h" className="text-2xl md:text-3xl font-bold">Our accessibility commitment</h2>
            <p className="mt-3 text-ink">We design for keyboard, screen reader, magnification and voice users from the beginning. We are working towards WCAG 2.2 Level AA and will publish results once full automated and manual audits are complete. You can adjust text size, contrast, motion and spacing at any time.</p>
            <Link to="/accessibility" className="link mt-4 inline-flex min-h-[44px] items-center font-semibold">Read our accessibility statement</Link>
          </div>
        </div>
      </section>
    </>
  );
}
